import React, { useEffect, useState, useRef } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import AppleIcon from '@mui/icons-material/Apple';
import styled from "@emotion/styled";
import useCart from "../hooks/useCart";

const GooglePayButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  padding: 0;
  height: 44px;
  min-width: 200px;
  width: 100%;
  cursor: pointer;
  font-family: 'Google Sans', 'Roboto', arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.25px;
  line-height: 1.2;
  color: #3c4043;
  background-color: #fff;
  transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:hover:not(:disabled) {
    background-color: #f8f9fa;
    box-shadow: 0 1px 1px 0 rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
  }

  &:active:not(:disabled) {
    background-color: #e8eaed;
    transform: scale(0.98);
    transition: transform 0.1s ease-in-out;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ApplePayButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: none;
  border-radius: 4px;
  padding: 0;
  height: 44px;
  min-width: 200px;
  width: 100%;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 17px;
  font-weight: 400;
  letter-spacing: -0.022em;
  line-height: 1.2;
  color: white;
  background-color: #000;
  transition: background-color 0.15s ease-in-out;

  &:hover:not(:disabled) {
    background-color: #1a1a1a;
  }

  &:active:not(:disabled) {
    background-color: #333;
    transform: scale(0.98);
    transition: transform 0.1s ease-in-out;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const GooglePayLogo = () => (
  <svg viewBox="-3 0 262 262" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027" fill="#4285F4"></path><path d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1" fill="#34A853"></path><path d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782" fill="#FBBC05"></path><path d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251" fill="#EB4335"></path></g></svg>
);

const PaymentRequestButton = ({
  provider = "google",
  amount,
  onPaymentAuthorized,
  disabled,
  style = {},
  showSeparator,
}) => {
  const stripe = useStripe();
  const paymentRequestRef = useRef(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const onPaymentAuthorizedRef = useRef(onPaymentAuthorized);
  const { summary, shippingReady } = useCart();

  const isGoogle = provider === "google";
  const isApple = provider === "apple";

  // Use default separator values based on provider if not explicitly set
  const shouldShowSeparator = showSeparator !== undefined ? showSeparator : (isApple ? true : false);

  useEffect(() => {
    onPaymentAuthorizedRef.current = onPaymentAuthorized;
  }, [onPaymentAuthorized]);

  useEffect(() => {
    if (!stripe || !amount || amount <= 0) return;

    // Wait for shipping details to be ready if we need shipping
    if (!shippingReady) return;

    // Only create payment request if it doesn't exist
    if (paymentRequestRef.current) return;

    // Calculate shipping cost from cart summary
    const shippingCost = (summary?.calculatedShippingCost || 0) + (summary?.perishableAmount || 0);
    const shippingAmountInCents = Math.round(shippingCost * 100);
    const shippingLabel = shippingCost === 0 ? "Free Shipping" : "Standard Shipping";

    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Full Proof Order",
        amount: Math.round(amount * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestShipping: false,
      shippingOptions: [
        {
          id: "standard",
          label: shippingLabel,
          detail: "3-5 business days",
          amount: shippingAmountInCents,
        },
      ],
    });

    pr.on("shippingaddresschange", async (ev) => {
      ev.updateWith({ status: "success" });
    });

    pr.on("paymentmethod", async (ev) => {
      try {
        const result = await onPaymentAuthorizedRef.current({
          paymentMethod: ev.paymentMethod,
          payerName: ev.payerName,
          payerEmail: ev.payerEmail,
          shippingAddress: ev.shippingAddress,
          complete: ev.complete,
        });

        if (result.error) {
          ev.complete("fail");
        } else {
          ev.complete("success");
        }
      } catch (error) {
        ev.complete("fail");
        console.error(`${isGoogle ? "Google" : "Apple"} Pay error:`, error);
      }
    });

    pr.canMakePayment().then((result) => {
      const canMake = isGoogle
        ? (result && result.googlePay)
        : (result && result.applePay);

      if (canMake) {
        setCanMakePayment(true);
        paymentRequestRef.current = pr;
      }
    });
  }, [stripe, isGoogle, shippingReady]);

  useEffect(() => {
    if (paymentRequestRef.current && amount > 0) {
      paymentRequestRef.current.update({
        total: {
          label: "Full Proof Order",
          amount: Math.round(amount * 100),
        },
      });
    }
  }, [amount]);

  if (!canMakePayment || !paymentRequestRef.current) {
    return null;
  }

  const Button = isGoogle ? GooglePayButton : ApplePayButton;

  return (
    <>
      <div style={style}>
        <Button
          type="button"
          onClick={() => !disabled && paymentRequestRef.current.show()}
          disabled={disabled}
        >
          {isGoogle ? <GooglePayLogo /> : <AppleIcon />}
          <span>Pay</span>
        </Button>
      </div>
      {shouldShowSeparator && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            margin: "20px 0",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, height: 1, backgroundColor: "#e0e0e0" }} />
          <span style={{ color: "#666", fontSize: 14 }}>or pay with card</span>
          <div style={{ flex: 1, height: 1, backgroundColor: "#e0e0e0" }} />
        </div>
      )}
    </>
  );
};

export default PaymentRequestButton;
